/* Optional US address suggestions for the Voler intake and sample forms. */
(() => {
  'use strict';

  const MIN_LENGTH = 5;
  const WAIT_MS = 450;
  const LIMIT = 5;

  function init(root) {
    if (root.dataset.vsrAddressLookupReady) return;
    root.dataset.vsrAddressLookupReady = 'true';
    const key = root.dataset.vsrGeoapifyKey;
    if (!key) return; // Manual entry is always available without a configured key.

    const intake = root.hasAttribute('data-vfi');
    const field = name => root.querySelector(intake ? `[data-vsr-intake="${name}"]` : `[data-vsr-field="${name}"]`);
    const address = field(intake ? 'artAddress1' : 'address1');
    const city = field(intake ? 'artCity' : 'city');
    const state = field(intake ? 'artState' : 'state');
    const zip = field(intake ? 'artZip' : 'zip');
    if (!address || !city || !state || !zip) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'vsr__address-lookup';
    address.parentNode.insertBefore(wrapper, address);
    wrapper.append(address);
    const list = document.createElement('ul');
    list.className = 'vsr__address-suggestions';
    list.id = `${address.id}-suggestions`;
    list.setAttribute('aria-label', 'Suggested street addresses');
    list.hidden = true;
    wrapper.append(list);
    address.setAttribute('aria-controls', list.id);
    address.setAttribute('aria-expanded', 'false');
    const status = document.createElement('small');
    status.className = 'vsr__address-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    wrapper.insertAdjacentElement('afterend', status);
    const help = document.createElement('small');
    help.className = 'vsr__address-credit';
    help.innerHTML = 'Powered by <a href="https://www.geoapify.com/" target="_blank" rel="noopener noreferrer">Geoapify</a> · <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap contributors</a>.';
    status.insertAdjacentElement('afterend', help);

    let timer;
    let controller;
    let sequence = 0;
    let options = [];
    let selecting = false;

    function close() {
      options = [];
      list.replaceChildren();
      list.hidden = true;
      address.setAttribute('aria-expanded', 'false');
    }

    function assign(input, value) {
      if (!value) return;
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function select(result) {
      clearTimeout(timer);
      controller?.abort();
      sequence += 1;
      selecting = true;
      assign(address, result.address_line1);
      assign(city, result.city);
      assign(state, result.state_code);
      assign(zip, result.postcode);
      selecting = false;
      close();
      status.classList.remove('vsr__address-status--error');
      status.textContent = 'Address suggestion selected. Please confirm the details below.';
      address.focus();
    }

    function render(results) {
      close();
      options = results.map(result => ({
        ...result,
        city: result.city || result.town || result.village,
        state_code: String(result.state_code || '').replace(/^US-/i, '').toUpperCase(),
      })).filter(result => String(result.country_code || '').toLowerCase() === 'us' && result.housenumber && result.street &&
        result.address_line1 && result.city && result.state_code && /^\d{5}(?:-\d{4})?$/.test(result.postcode || '') &&
        [...state.options].some(option => option.value === result.state_code)).slice(0, LIMIT);
      if (!options.length) {
        status.classList.add('vsr__address-status--error');
        status.textContent = 'No complete address matches. Please enter the address manually.';
        return;
      }
      status.classList.remove('vsr__address-status--error');
      for (const result of options) {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = `${result.address_line1}, ${result.city}, ${result.state_code} ${result.postcode}`;
        button.addEventListener('click', () => select(result));
        item.append(button);
        list.append(item);
      }
      list.hidden = false;
      address.setAttribute('aria-expanded', 'true');
      status.textContent = `${options.length} address ${options.length === 1 ? 'suggestion' : 'suggestions'} available. Use the down arrow or Tab to review.`;
    }

    address.addEventListener('input', () => {
      if (selecting) return;
      clearTimeout(timer);
      controller?.abort();
      const request = ++sequence;
      close();
      status.classList.remove('vsr__address-status--error');
      status.textContent = '';
      const query = address.value.trim();
      if (query.length < MIN_LENGTH) return;
      timer = setTimeout(async () => {
        controller = new AbortController();
        status.textContent = 'Finding address suggestions…';
        const url = new URL('https://api.geoapify.com/v1/geocode/autocomplete');
        url.search = new URLSearchParams({ text: query, format: 'json', filter: 'countrycode:us', limit: String(LIMIT), apiKey: key }).toString();
        try {
          const response = await fetch(url, { signal: controller.signal, referrerPolicy: 'strict-origin-when-cross-origin' });
          if (!response.ok) {
            if (request === sequence) {
              status.classList.add('vsr__address-status--error');
              status.textContent = 'Suggestions are unavailable. Please enter the address manually.';
            }
            return;
          }
          const data = await response.json();
          if (request === sequence && address.value.trim() === query && Array.isArray(data.results)) render(data.results);
        } catch (error) {
          if (error.name !== 'AbortError' && request === sequence) {
            status.classList.add('vsr__address-status--error');
            status.textContent = 'Suggestions are unavailable. Please enter the address manually.';
          }
        }
      }, WAIT_MS);
    });
    address.addEventListener('keydown', event => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowDown' && !list.hidden) {
        event.preventDefault();
        list.querySelector('button')?.focus();
      }
    });
    list.addEventListener('keydown', event => {
      if (event.key === 'Escape') { close(); address.focus(); }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const buttons = [...list.querySelectorAll('button')];
      const index = buttons.indexOf(document.activeElement);
      buttons[(index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length]?.focus();
    });
    document.addEventListener('click', event => {
      if (!wrapper.contains(event.target)) close();
    });
  }

  document.querySelectorAll('[data-vfi], [data-vsr]').forEach(init);
  document.addEventListener('shopify:section:load', event =>
    event.target.querySelectorAll('[data-vfi], [data-vsr]').forEach(init));
})();
